/**
 * 菜单结构管理组件
 * 只负责菜单的层级结构、路径、图标等，不涉及权限逻辑
 * 权限通过 permission_code 字段绑定到权限系统的权限点
 */
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Input,
  Select,
  Tree,
  message,
  Popconfirm,
  Tooltip,
  Row,
  Col,
  Statistic,
  Divider,
  Alert
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  SettingOutlined,
  LinkOutlined,
  MenuOutlined,
  FolderOutlined,
  FileOutlined
} from '@ant-design/icons';
import * as AntIcons from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import menuConfigService from '../../../services/menuConfigService';

const { Option } = Select;
const { TextArea } = Input;

interface MenuItem {
  id: number;
  name: string;
  name_en?: string;
  i18n_key?: string;
  path: string;
  icon: string;
  parent?: number | null;
  sort_order: number;
  is_active: boolean;
  permission_code?: string; // 绑定到权限系统的权限点
  description?: string;
  children?: MenuItem[];
}

const MenuStructureManagement: React.FC = () => {
  const { t } = useTranslation();
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMenu, setEditingMenu] = useState<MenuItem | null>(null);
  const [form] = Form.useForm();
  // 分页受控，确保每页数量切换生效
  const [pageSize, setPageSize] = useState<number>(20);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // 常用图标列表（扩充 Outlined/Filled/TwoTone 常见场景）
  const ICON_NAMES: string[] = [
    // 基础 & 导航
    'HomeOutlined','HomeFilled','AppstoreOutlined','MenuOutlined','UnorderedListOutlined','BarsOutlined',
    // 用户 & 团队
    'UserOutlined','UserAddOutlined','TeamOutlined','IdcardOutlined','SafetyOutlined','CrownOutlined',
    // 业务 & 资源
    'ShoppingCartOutlined','ShopOutlined','GiftOutlined','TagsOutlined','CreditCardOutlined','BankOutlined',
    'DatabaseOutlined','CloudOutlined','FolderOpenOutlined','FileTextOutlined','FileOutlined','ContainerOutlined','InboxOutlined',
    // 系统 & 设置
    'SettingOutlined','ToolOutlined','ApiOutlined','ControlOutlined','DeploymentUnitOutlined','ThunderboltOutlined',
    // 统计 & 可视化
    'BarChartOutlined','LineChartOutlined','PieChartOutlined','AreaChartOutlined','DotChartOutlined',
    // 消息 & 日历
    'BellOutlined','NotificationOutlined','CalendarOutlined','ScheduleOutlined','AlertOutlined',
    // 状态 & 结果
    'CheckCircleOutlined','CloseCircleOutlined','InfoCircleOutlined','QuestionCircleOutlined',
    'ExclamationCircleOutlined','WarningOutlined','StarOutlined','LikeOutlined','DislikeOutlined',
    // 位置 & 设备
    'EnvironmentOutlined','GlobalOutlined','MobileOutlined','TabletOutlined','LaptopOutlined',
    // 流程/业务流（入库/出库/物流/指令/库存）
    'ImportOutlined',   // 入库
    'ExportOutlined',   // 出库
    'CarOutlined',      // 物流（车辆）
    'TruckOutlined',    // 物流（卡车）
    'DeliveredProcedureOutlined', // 配送流程
    'OrderedListOutlined', // 指令/工单
    'AuditOutlined',       // 审核/指令流
    'FileDoneOutlined',    // 已完成指令
    'ReconciliationOutlined', // 对账/流程结算
    'HddOutlined',         // 库存/存储
    'BoxPlotOutlined',     // 箱/库存单元
    'InteractionOutlined', // 交互/流转
    // TwoTone 常用
    'HomeTwoTone','SettingTwoTone','StarTwoTone','HeartTwoTone','SmileTwoTone','CheckCircleTwoTone','CloseCircleTwoTone',
    'InfoCircleTwoTone','ExclamationCircleTwoTone','BellTwoTone','CalendarTwoTone','FolderOpenTwoTone','FileTwoTone',
    // 资金 & 价格
    'DollarCircleOutlined','PayCircleOutlined','WalletOutlined',
  ];

  // 权限点选项（从权限系统获取）
  const PERMISSION_OPTIONS = [
    { value: 'warehouse:read', label: '仓库管理-查看' },
    { value: 'warehouse:create', label: '仓库管理-创建' },
    { value: 'warehouse:update', label: '仓库管理-更新' },
    { value: 'warehouse:delete', label: '仓库管理-删除' },
    { value: 'project:read', label: '项目管理-查看' },
    { value: 'project:create', label: '项目管理-创建' },
    { value: 'project:update', label: '项目管理-更新' },
    { value: 'project:delete', label: '项目管理-删除' },
    { value: 'customer:read', label: '客户管理-查看' },
    { value: 'customer:create', label: '客户管理-创建' },
    { value: 'customer:update', label: '客户管理-更新' },
    { value: 'customer:delete', label: '客户管理-删除' },
    { value: 'inventory:read', label: '库存管理-查看' },
    { value: 'inventory:create', label: '库存管理-创建' },
    { value: 'inventory:update', label: '库存管理-更新' },
    { value: 'inventory:delete', label: '库存管理-删除' },
    { value: 'user:read', label: '用户管理-查看' },
    { value: 'user:create', label: '用户管理-创建' },
    { value: 'user:update', label: '用户管理-更新' },
    { value: 'user:delete', label: '用户管理-删除' },
    { value: 'system:admin', label: '系统管理-管理员' },
  ];

  useEffect(() => {
    loadMenus();
  }, []);

  const loadMenus = async () => {
    setLoading(true);
    try {
      console.log('🔄 开始加载菜单结构数据...');
      const response = await menuConfigService.getMenuItems();
      console.log('📊 菜单结构数据:', response);
      // 直接使用 menu_config 的返回（包含 parent 主键）
      setMenus(response as any);
      console.log('✅ 菜单结构数据加载完成');
    } catch (error) {
      console.error('❌ 加载菜单结构失败:', error);
      message.error('加载菜单结构失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingMenu(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: MenuItem) => {
    setEditingMenu(record);
    form.setFieldsValue({
      ...record,
      parent: record.parent || undefined
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      console.log('🗑️ 删除菜单:', id);
      await menuConfigService.deleteMenuItem(id);
      message.success('菜单删除成功');
      loadMenus();
    } catch (error) {
      console.error('❌ 删除菜单失败:', error);
      message.error('删除菜单失败');
    }
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      console.log('💾 保存菜单:', values);
      if (editingMenu) {
        console.log('🔄 更新菜单:', editingMenu.id);
        const payload = {
          name: values.name,
          name_en: values.name_en,
          i18n_key: values.i18n_key,
          path: values.path || '',
          icon: values.icon || '',
          parent: values.parent ?? null,
          sort_order: Number(values.sort_order ?? 0),
          is_active: !!values.is_active,
          description: values.description || '',
        } as any;
        await menuConfigService.updateMenuItem(editingMenu.id, payload);
        message.success('菜单更新成功');
      } else {
        console.log('➕ 创建菜单');
        const payload = {
          name: values.name,
          name_en: values.name_en,
          i18n_key: values.i18n_key,
          path: values.path || '',
          icon: values.icon || '',
          parent: values.parent ?? null,
          sort_order: Number(values.sort_order ?? 0),
          is_active: !!values.is_active,
          description: values.description || '',
        } as any;
        await menuConfigService.createMenuItem(payload);
        message.success('菜单创建成功');
      }
      setModalVisible(false);
      loadMenus();
    } catch (error) {
      console.error('❌ 保存菜单失败:', error);
      message.error(editingMenu ? '菜单更新失败' : '菜单创建失败');
    } finally {
      setLoading(false);
    }
  };

  // 构建树形数据
  const buildTreeData = (menus: MenuItem[]): MenuItem[] => {
    const menuMap = new Map<number, MenuItem>();
    const rootMenus: MenuItem[] = [];

    // 创建映射
    menus.forEach(menu => {
      menuMap.set(menu.id, { ...menu, children: [] });
    });

    // 构建树形结构
    menus.forEach(menu => {
      const menuNode = menuMap.get(menu.id)!;
      const parentId = (menu as any).parent as number | null | undefined;
      if (parentId && menuMap.has(parentId)) {
        const parent = menuMap.get(parentId)!;
        parent.children!.push(menuNode);
      } else {
        rootMenus.push(menuNode);
      }
    });

    // 按 sort_order, id 对同级进行排序，并递归处理
    const sortNodes = (nodes: MenuItem[]) => {
      nodes.sort((a, b) => {
        const so = (a.sort_order ?? 0) - (b.sort_order ?? 0);
        if (so !== 0) return so;
        return (a.id ?? 0) - (b.id ?? 0);
      });
      nodes.forEach(n => {
        if (Array.isArray(n.children) && n.children.length > 0) {
          sortNodes(n.children);
        }
      });
    };
    sortNodes(rootMenus);

    return rootMenus;
  };

  // 过滤树（节点或其任一后代命中关键词则保留）。匹配 name/name_en/path
  const filterTree = (nodes: MenuItem[], query: string): MenuItem[] => {
    if (!query) return nodes;
    const q = query.toLowerCase();
    const matchNode = (n: MenuItem) =>
      (n.name || '').toLowerCase().includes(q) ||
      (n.name_en || '').toLowerCase().includes(q) ||
      (n.path || '').toLowerCase().includes(q);
    const dfs = (list: MenuItem[]): MenuItem[] => {
      const result: MenuItem[] = [];
      list.forEach(n => {
        const children = Array.isArray(n.children) ? dfs(n.children) : [];
        if (matchNode(n) || children.length > 0) {
          result.push({ ...n, children });
        }
      });
      return result;
    };
    return dfs(nodes);
  };

  const treeData = useMemo(() => {
    const fullTree = buildTreeData(menus);
    return filterTree(fullTree, searchQuery);
  }, [menus, searchQuery]);

  // 计算需要展开的所有父级行（保持始终展开）
  const computeExpandedKeys = (nodes: MenuItem[]): React.Key[] => {
    const keys: React.Key[] = [];
    const dfs = (list: MenuItem[]) => {
      list.forEach(n => {
        if (Array.isArray(n.children) && n.children.length > 0) {
          keys.push(n.id);
          dfs(n.children);
        }
      });
    };
    dfs(nodes);
    return keys;
  };

  const columns = [
    {
      title: '菜单名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string, record: MenuItem) => (
        <Space>
          <span>{text}</span>
          {record.i18n_key ? <Tag color="blue">{record.i18n_key}</Tag> : null}
        </Space>
      ),
    },
    {
      title: '路径',
      dataIndex: 'path',
      key: 'path',
      width: 200,
      render: (text: string) => (
        <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>
          {text}
        </code>
      ),
    },
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      render: (icon: string) => {
        const IconComp = (icon && (AntIcons as any)[icon]) ? (AntIcons as any)[icon] : undefined;
        return (
          <Space>
            {IconComp ? <IconComp /> : <FileOutlined />}
            <span>{icon || '-'}</span>
          </Space>
        );
      },
    },
    {
      title: '权限绑定',
      dataIndex: 'permission_code',
      key: 'permission_code',
      width: 200,
      render: (permissionCode: string) => (
        permissionCode ? (
          <Tag color="green" icon={<LinkOutlined />}>
            {permissionCode}
          </Tag>
        ) : (
          <Tag color="default">无权限绑定</Tag>
        )
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_, record: MenuItem) => (
        <Space>
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个菜单吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 统计数据
  const totalMenus = menus.length;
  const activeMenus = menus.filter(m => m.is_active).length;
  const menusWithPermission = menus.filter(m => m.permission_code).length;

  return (
    <div className="menu-structure-management">
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="总菜单数"
              value={totalMenus}
              prefix={<MenuOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="启用菜单"
              value={activeMenus}
              prefix={<FolderOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="已绑定权限"
              value={menusWithPermission}
              prefix={<LinkOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Input.Search
              placeholder="搜索菜单（名称/英文名/路径）"
              allowClear
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 320 }}
            />
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            添加菜单
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={treeData}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10','20','50','100'],
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) setPageSize(size);
            },
            onShowSizeChange: (page, size) => {
              setCurrentPage(1);
              setPageSize(size);
            },
          }}
          expandable={{
            expandedRowKeys: computeExpandedKeys(treeData),
            onExpandedRowsChange: () => {},
            expandIcon: () => <span style={{ display: 'inline-block', width: 16 }} />,
            indentSize: 16,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editingMenu ? '编辑菜单' : '添加菜单'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="菜单名称（中文）"
                rules={[{ required: true, message: '请输入菜单名称' }]}
              >
                <Input placeholder="请输入菜单名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name_en"
                label="菜单名称（英文）"
                rules={[{ required: true, message: '请输入英文名称' }]}
              >
                <Input placeholder="Enter English name" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="i18n_key"
                label="多语言Key（可选）"
                tooltip="如 menu.project.list，前端将用 t(i18n_key) 显示"
              >
                <Input placeholder="menu.xxx.yyy" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="path"
                label="路径"
                tooltip="父级菜单可留空（仅用于展开，不跳转）"
              >
                <Input placeholder="/dashboard/example" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="icon"
                label="图标（可选）"
              >
                <Select
                  placeholder="选择图标（可选）"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                >
                  {ICON_NAMES.map(iconName => (
                      <Option key={iconName} value={iconName} label={iconName}>
                        <Space>
                          {/* 动态渲染图标样式 */}
                          {(() => { try { const icons = require('@ant-design/icons'); const C = icons[iconName]; return C ? <C/> : <span/>; } catch { return <span/>; } })()}
                          <span>{iconName}</span>
                        </Space>
                      </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="parent"
                label="父菜单"
              >
                <Select
                  placeholder="选择父菜单（可选）"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) => {
                    const label = ((option?.label as string) || '').toLowerCase();
                    const val = String(option?.value ?? '').toLowerCase();
                    const key = ((option as any)?.key || '').toLowerCase();
                    const q = input.toLowerCase();
                    return label.includes(q) || val.includes(q) || key.includes(q);
                  }}
                >
                  {menus
                    .filter(menu => !editingMenu || menu.id !== editingMenu.id)
                    .map(menu => (
                      <Option key={menu.id} value={menu.id} label={`${menu.name}${menu.name_en ? ' ' + menu.name_en : ''}${menu.path ? ' ' + menu.path : ''}`}>
                        {menu.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="sort_order"
                label="排序"
                initialValue={0}
              >
                <Input type="number" placeholder="排序数字" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="permission_code"
            label="权限绑定"
            extra="绑定到权限系统中的权限点，用于控制菜单显示"
          >
            <Select placeholder="选择权限点（可选）" allowClear>
              {PERMISSION_OPTIONS.map(permission => (
                <Option key={permission.value} value={permission.value}>
                  {permission.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={3} placeholder="菜单描述（可选）" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="状态"
            valuePropName="checked"
            initialValue={true}
          >
            <div>
              <label>
                <input type="checkbox" defaultChecked /> 启用菜单
              </label>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuStructureManagement;
